import "./TermsAndConditions.scss";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, type FetchResult } from "@apollo/client";
import {
  SET_TERMS_STATUS,
  TERMS_OF_SERVICE_LATEST_VERSION,
  type SetTermsStatusResponse,
  type SetTermsStatusVariables,
  type TermsOfServiceLatestVersionResponse,
} from "./TermsAndConditionsApi";
import { Document, Page, pdfjs } from "react-pdf";
import {
  Loader,
  TextButton,
  useIcon,
  useMutationWrapper,
  ModalContext,
  showErrorDialog,
  getLocalDateTime,
} from "@SeemplicityDev/shared";
import { showToast } from "../Forms/Dialogs/Toast/Toast";

pdfjs.GlobalWorkerOptions.workerSrc = "pdf.worker.min.js";

interface TermsAndConditionsProps {
  showReminderBtn: boolean;
}

const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({
  showReminderBtn,
}) => {
  const { getSvg } = useIcon();
  const { t } = useTranslation();
  const { closeModal } = useContext(ModalContext);
  const { data: tosData } = useQuery<TermsOfServiceLatestVersionResponse>(
    TERMS_OF_SERVICE_LATEST_VERSION
  );
  const [termsPDF, setTermsPDF] = useState<string | undefined>(undefined);
  const [setTermsStatus] = useMutationWrapper<
    SetTermsStatusResponse,
    SetTermsStatusVariables
  >(SET_TERMS_STATUS);
  const [fileName, setFileName] = useState("seemplicity_t&c_");

  useEffect(() => {
    if (!!tosData) {
      setTermsPDF(
        `data:application/pdf;base64,${tosData?.terms_of_service_latest_version?.file}`
      );

      setFileName(
        `${fileName}${getLocalDateTime(
          tosData?.terms_of_service_latest_version?.created_time ?? ""
        ).toFormat("ddMMyy")}`
      );
    }
  }, [tosData]);

  const [numPages, setNumPages] = useState<number | null>(null);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const invokeSetTermsStatus = (
    isSigned: boolean,
    setReminder: boolean,
    termsVersion: string | undefined
  ) => {
    setTermsStatus({
      variables: {
        set_sign: isSigned,
        set_reminder: setReminder,
        terms_version: termsVersion as string,
      },
    })
      .then((res: FetchResult<SetTermsStatusResponse>) => {
        closeModal();
        showToast({ message: res?.data?.set_terms_status?.msg });
      })
      .catch((err: { message?: string }) => {
        showErrorDialog({ message: err.message });
      });
  };

  return (
    <div className={"terms-container"}>
      {!termsPDF ? (
        <Loader center img={getSvg("icon-spinner")} iconSize={"xxxxl"} />
      ) : (
        <>
          <div className={"instructions-and-download"}>
            {t("Please review and confirm the terms and conditions below.")}
            <a href={termsPDF} className={"download"} download={fileName}>
              <i className={`seem-icon seem-icon-download`} />
              {t("Download")}
            </a>
          </div>
          <div className={"terms-content"}>
            <Document file={termsPDF} onLoadSuccess={onDocumentLoadSuccess}>
              {Array.from(new Array(numPages), (el, index) => (
                <Page key={`page_${index + 1}`} pageNumber={index + 1} />
              ))}
            </Document>
          </div>
          <div className={"terms-modal-buttons"}>
            <TextButton
              data-testid={"agree-terms"}
              buttonStyle={"primary"}
              label={t("I Agree")}
              size={"small"}
              onClick={() =>
                invokeSetTermsStatus(
                  true,
                  false,
                  tosData?.terms_of_service_latest_version?.version
                )
              }
            />
            {showReminderBtn && (
              <TextButton
                data-testid={"snooze-terms"}
                label={t("Remind me in 7 days")}
                size={"small"}
                onClick={() => invokeSetTermsStatus(false, true, "")}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};
export default TermsAndConditions;
